import type { Department } from "../../contexts/AuthProvider";
import { type CensusDisplayGroup, censusDisplayGroups, displayGroupKey } from "../../utils/censusDisplayGroups";
import { byDimensionsOf } from "../../utils/censusResolution";
import { type DepartmentLocation, type HospitalData, LocationMode, type NurseInShift } from "../../utils/models";
import type { AiPrintoutLocationMetrics, AiPrintoutMetrics } from "./contract";

/** The Day view's `findDepartmentLocationForNurse` (from `useHospitalData`): which configured location a shift is at. */
export type FindDepartmentLocation = (shift: NurseInShift, locations: DepartmentLocation[]) => DepartmentLocation | undefined;

const GROUPING_OF_MODE: Record<LocationMode, AiPrintoutMetrics["byLocationGrouping"]> = {
	[LocationMode.None]: null,
	[LocationMode.Floor]: "floor",
	[LocationMode.Unit]: "unit",
	[LocationMode.Both]: "unitAndFloor",
	[LocationMode.Auto]: "location",
};

/** One Day view census group, with the shifts the staff table puts under it. */
export type AiPrintoutCensusGroup = {
	fields: Pick<AiPrintoutLocationMetrics, "floor" | "unit" | "location">;
	census: number;
	shifts: NurseInShift[];
};

interface CensusGroupsArgs {
	department: Department;
	hospitalData: HospitalData;
	weekIndex: number;
	dayIndex: number;
	/** The viewer's census grouping (`settings.censusByLocation`), which decides the Day view's groups. */
	mode: LocationMode;
	totalCensus: number;
	shifts: NurseInShift[];
	findDepartmentLocation: FindDepartmentLocation;
}

/** A group's own key fields: Floor, Unit and Both groups have exactly one part, the area the mode names. */
const fieldsOf = (group: CensusDisplayGroup, mode: LocationMode): AiPrintoutCensusGroup["fields"] => {
	const [area] = group.parts;
	if (mode === LocationMode.Auto) return { floor: null, unit: null, location: group.key };
	return { floor: mode === LocationMode.Unit ? null : area.floor, unit: mode === LocationMode.Floor ? null : area.unit, location: null };
};

/**
 * The Day view staff table's census groups for the viewer's grouping: `censusDisplayGroups` for each group's
 * header census, and `displayGroupKey` of the shift's location (`findDepartmentLocationForNurse`) for who is in
 * it — the two halves `useCensusGrouping` builds its groups from. Like the table, a group with neither census
 * nor people is left out; unlike it, shifts in no group (its Unassigned bucket) are too. Null for no grouping.
 */
export const censusGroupsOf = (args: CensusGroupsArgs): { grouping: AiPrintoutMetrics["byLocationGrouping"]; groups: AiPrintoutCensusGroup[] } | null => {
	const { department, hospitalData, weekIndex, dayIndex, mode } = args;
	const grouping = GROUPING_OF_MODE[mode];
	if (grouping === null) return null;
	const byDimensions = byDimensionsOf(hospitalData.weeks[weekIndex].weeklyShifts[0]?.days[dayIndex]?.census_grouping);
	const keyedShifts = args.shifts.map((shift) => {
		const location = args.findDepartmentLocation(shift, department.locations);
		return { shift, key: location === undefined ? undefined : displayGroupKey(location, mode) };
	});
	const groups = censusDisplayGroups(mode, byDimensions, args.totalCensus, department.locations, department.statsMode)
		.map((group) => ({
			fields: fieldsOf(group, mode),
			census: group.census,
			shifts: keyedShifts.filter(({ key }) => key === group.key).map(({ shift }) => shift),
		}))
		.filter((group) => group.census > 0 || group.shifts.length > 0);
	return { grouping, groups };
};
